/* ============================================================
   IMANUEL — cross bundle (any top + any bottom, % off the pair)
   Standalone from imanuel-handoff.js so the existing "Bundle & Save"
   builder keeps its own state. Shares the toast + cart drawer that
   handoff.js exposes on window.
   ============================================================ */
(function () {
  const I18N = (window.IMANUEL_I18N && window.IMANUEL_I18N.cross_bundle) || {};
  /** Falls back to the English original, and rejects Shopify's "translation missing:" sentinel. */
  const t = (key, fallback) => {
    const val = I18N[key];
    return typeof val === "string" && val && val.indexOf("translation missing") !== 0 ? val : fallback;
  };
  const tf = (key, fallback, vars) =>
    t(key, fallback).replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k] != null ? vars[k] : ""));

  /* Money nodes here carry data-skip-fx, so the display-currency walker leaves
     them alone and we repaint them ourselves on the currency-change event —
     the same contract renderBundle() uses for the Bundle & Save widget. */
  const moneyFromCents = (c) =>
    window.imanuelDisplayCurrency?.formatUsdCents?.(c)
    || "$" + (c / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const toast = (msg) => {
    if (typeof window.imanuelToast === "function") return window.imanuelToast(msg);
    console.log("[cross-bundle]", msg);
  };

  /** Cards are narrow — drop the brand prefix the way the Bundle & Save picker does. */
  const shortTitle = (title) => String(title || "").replace(/^IMANU[ÉE]L\s+/i, "").trim() || title;

  const escapeHtml = (str) =>
    String(str == null ? "" : str).replace(/[&<>"']/g, (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

  /** Repaint hooks for every configurator on the page (used on currency change). */
  const instances = [];

  /* ---------- catalog ---------- */

  /** Flatten one `{subtitle, data}` catalog entry into a single product object. */
  function normalize(entry) {
    const p = entry && entry.data;
    if (!p || !p.id) return null;
    return {
      id: p.id,
      title: p.title || "",
      handle: p.handle || "",
      url: p.url || "",
      subtitle: entry.subtitle || "",
      priceCents: Number(p.price_cents) || 0,
      image: p.image || null,
      colors: (p.options && p.options.colors) || [],
      sizes: (p.options && p.options.sizes) || [],
      variants: p.variants || [],
    };
  }

  function readCatalog(root) {
    // The <script> is a sibling of the section, not a descendant.
    const el = root.previousElementSibling?.id === "cross-bundle-catalog"
      ? root.previousElementSibling
      : document.getElementById("cross-bundle-catalog");
    if (!el) return null;
    let parsed;
    try {
      parsed = JSON.parse(el.textContent || "{}");
    } catch (_) {
      return null;
    }
    const tops = (parsed.tops || []).map(normalize).filter(Boolean);
    const bottoms = (parsed.bottoms || []).map(normalize).filter(Boolean);
    if (!tops.length || !bottoms.length) return null;
    return {
      tops,
      bottoms,
      percent: Math.max(0, Math.min(100, Number(parsed.discount?.percent) || 0)),
      code: String(parsed.discount?.code || "").trim(),
    };
  }

  /* ---------- variant helpers ---------- */

  function findVariant(product, color, size) {
    return (
      product.variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        return (!color || opts.includes(color)) && (!size || opts.includes(size));
      }) || null
    );
  }

  function variantAvailable(product, color, size) {
    const v = findVariant(product, color, size);
    return !!(v && v.available);
  }

  function colorAvailable(product, color) {
    if (!product.sizes.length) return variantAvailable(product, color, null);
    return product.sizes.some((s) => variantAvailable(product, color, s.name));
  }

  /** Waist-style sizing (W28, 32) reads as "waist" rather than "size" in the UI. */
  function sizeNoun(product) {
    if (!product.sizes.length) return "";
    const waistish = product.sizes.every((s) => /^w?\s*\d{2,3}$/i.test(String(s.name).trim()));
    return waistish ? t("waist", "waist") : t("size", "size");
  }

  function isLightHex(hex) {
    const h = String(hex || "").replace("#", "");
    if (h.length !== 6) return false;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.82;
  }

  /** Ask the Shopify CDN for a smaller rendition so option thumbs stay light. */
  function imageAt(url, w) {
    if (!url) return "";
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, `_${w}x$1$2`);
  }

  /* ---------- one configurator instance ---------- */

  function initCrossBundle(root) {
    if (root.dataset.xbundleReady === "1") return;
    const catalog = readCatalog(root);
    if (!catalog) return;
    root.dataset.xbundleReady = "1";

    const slots = {
      top: { list: catalog.tops, product: null, color: null, size: null },
      bottom: { list: catalog.bottoms, product: null, color: null, size: null },
    };
    let adding = false;

    const el = (sel) => root.querySelector(sel);
    const stepEl = (slot) => root.querySelector(`[data-xstep="${slot}"]`);
    const stageEl = (slot) => root.querySelector(`[data-xstage="${slot}"]`);

    const priceEl = el("[data-xprice]");
    const compareEl = el("[data-xcompare]");
    const saveEl = el("[data-xsave]");
    const badgeEl = el("[data-xbadge]");
    const hintEl = el("[data-xhint]");
    const addBtn = el("[data-xadd]");
    const addLabel = el("[data-xadd-label]");

    /* ----- rendering ----- */

    function renderOptions(slot) {
      const state = slots[slot];
      const step = stepEl(slot);
      const wrap = step.querySelector("[data-xoptions]");
      const count = step.querySelector("[data-xcount]");

      count.textContent = tf("n_styles", "{count} styles", { count: state.list.length });
      // Two or three choices read better as full-width rows; more want a grid.
      wrap.classList.toggle("xbundle__options--wide", state.list.length <= 3);
      wrap.innerHTML = state.list
        .map((p) => {
          const active = state.product && state.product.id === p.id;
          const soldOut = !p.variants.some((v) => v.available);
          const thumb = p.image
            ? `<img src="${escapeHtml(imageAt(p.image, 260))}" alt="" loading="lazy" width="130" height="130">`
            : "";
          const subText = p.subtitle || (soldOut ? t("sold_out", "COMING SOON") : "");
          return `
            <button type="button" class="xbundle__opt${active ? " is-active" : ""}"
              role="radio" aria-checked="${active ? "true" : "false"}"
              data-xpick="${escapeHtml(p.handle)}"${soldOut ? " disabled" : ""}>
              <span class="xbundle__opt-thumb">${thumb}</span>
              <span class="xbundle__opt-text">
                <b class="xbundle__opt-title">${escapeHtml(shortTitle(p.title))}</b>
                ${subText ? `<small class="xbundle__opt-sub">${escapeHtml(subText)}</small>` : ""}
              </span>
              <span class="xbundle__opt-price" data-skip-fx="true">${moneyFromCents(p.priceCents)}</span>
            </button>`;
        })
        .join("");
    }

    function renderVariant(slot) {
      const state = slots[slot];
      const step = stepEl(slot);
      const box = step.querySelector("[data-xvariant]");
      const label = step.querySelector("[data-xvariant-label]");
      const colors = step.querySelector("[data-xcolors]");
      const sizes = step.querySelector("[data-xsizes]");
      const p = state.product;

      if (!p) {
        box.hidden = true;
        colors.innerHTML = "";
        sizes.innerHTML = "";
        return;
      }
      box.hidden = false;

      const parts = [];
      if (p.colors.length) parts.push(t("color", "color"));
      const noun = sizeNoun(p);
      if (noun) parts.push(noun);
      label.textContent = parts.length
        ? `${shortTitle(p.title)} — ${parts.join(" & ")}`
        : shortTitle(p.title);

      colors.innerHTML = p.colors
        .map((c) => {
          const active = state.color === c.name;
          const disabled = !colorAvailable(p, c.name);
          return `
            <button type="button"
              class="bundle-swatch xbundle__swatch${active ? " is-active" : ""}${isLightHex(c.hex) ? " is-light" : ""}"
              style="background:${escapeHtml(c.hex)}" title="${escapeHtml(c.name)}" aria-label="${escapeHtml(c.name)}"
              aria-pressed="${active ? "true" : "false"}"
              data-xcolor="${escapeHtml(c.name)}"${disabled ? " disabled" : ""}></button>`;
        })
        .join("");
      if (p.colors.length) {
        colors.insertAdjacentHTML(
          "beforeend",
          `<span class="xbundle__color-name">${escapeHtml(state.color || t("select_color", "Select a color"))}</span>`
        );
      }

      sizes.innerHTML = p.sizes
        .map((s) => {
          const active = state.size === s.name;
          const disabled = !variantAvailable(p, state.color, s.name);
          return `
            <button type="button" class="bundle-size xbundle__size${active ? " is-active" : ""}"
              aria-pressed="${active ? "true" : "false"}"
              data-xsize="${escapeHtml(s.name)}"${disabled ? " disabled" : ""}>${escapeHtml(s.name)}</button>`;
        })
        .join("");
    }

    function renderStage(slot) {
      const state = slots[slot];
      const stage = stageEl(slot);
      const img = stage.querySelector(".xbundle__stage-img");
      const title = stage.querySelector("[data-xstage-title]");
      const meta = stage.querySelector("[data-xstage-meta]");
      const p = state.product;

      if (!p) {
        img.hidden = true;
        img.removeAttribute("src");
        stage.classList.remove("is-filled");
        title.textContent = slot === "top"
          ? t("choose_a_top", "Choose a top")
          : t("choose_a_bottom", "Choose a bottom");
        meta.textContent = tf("n_styles_caps", "{count} STYLES", { count: state.list.length });
        return;
      }

      stage.classList.add("is-filled");
      const color = state.color ? p.colors.find((c) => c.name === state.color) : null;
      const src = (color && color.image) || p.image;
      if (src) {
        img.src = imageAt(src, 900);
        img.alt = p.title;
        img.hidden = false;
      } else {
        img.hidden = true;
        img.removeAttribute("src");
      }
      title.textContent = shortTitle(p.title);
      meta.textContent = [state.color, state.size].filter(Boolean).join(" · ").toUpperCase();
    }

    /** What the shopper still owes us, in the order the steps are presented. */
    function nextAction() {
      for (const slot of ["top", "bottom"]) {
        const state = slots[slot];
        if (!state.product) {
          return slot === "top"
            ? t("select_your_top", "SELECT YOUR TOP")
            : t("select_your_bottom", "SELECT YOUR BOTTOM");
        }
        if (state.product.colors.length && !state.color) {
          return t("select_color_size", "SELECT COLOR & SIZE");
        }
        if (state.product.sizes.length && !state.size) {
          return t("select_size", "SELECT SIZE");
        }
        if (!variantAvailable(state.product, state.color, state.size)) {
          return t("unavailable", "UNAVAILABLE");
        }
      }
      return null;
    }

    function renderCheckout() {
      const bothPicked = !!(slots.top.product && slots.bottom.product);
      const compareCents =
        (slots.top.product?.priceCents || 0) + (slots.bottom.product?.priceCents || 0);
      const bundleCents = bothPicked
        ? Math.round((compareCents * (100 - catalog.percent)) / 100)
        : compareCents;
      const saveCents = compareCents - bundleCents;
      const showSave = bothPicked && saveCents > 0;

      priceEl.textContent = moneyFromCents(bundleCents);
      compareEl.hidden = !showSave;
      compareEl.textContent = showSave ? moneyFromCents(compareCents) : "";
      saveEl.hidden = !showSave;
      saveEl.textContent = showSave
        ? `${t("save_word", "SAVE")} ${moneyFromCents(saveCents)}`
        : "";
      if (badgeEl) badgeEl.hidden = !showSave;

      const pending = nextAction();
      hintEl.textContent = pending
        ? tf("unlock_hint_pct", "Add a top and a bottom to unlock {percent}% off your bundle.", {
          percent: catalog.percent,
        })
        : tf("pair_ready", "{percent}% off applied to your pair.", { percent: catalog.percent });
      addBtn.disabled = !!pending;
      addLabel.textContent = pending
        ? pending
        : tf("add_bundle_price", "ADD BUNDLE — {price}", { price: moneyFromCents(bundleCents) });
    }

    function renderAll() {
      ["top", "bottom"].forEach((slot) => {
        renderOptions(slot);
        renderVariant(slot);
        renderStage(slot);
      });
      renderCheckout();
    }

    /* ----- picking ----- */

    /** Pre-pick the first in-stock color/size so a chosen product is one tap from ready. */
    function seedVariant(slot) {
      const state = slots[slot];
      const p = state.product;
      if (!p) return;
      if (p.colors.length && !state.color) {
        state.color = (p.colors.find((c) => colorAvailable(p, c.name)) || p.colors[0]).name;
      }
      if (!p.sizes.length) {
        state.size = null;
        return;
      }
      if (!state.size || !variantAvailable(p, state.color, state.size)) {
        const first = p.sizes.find((s) => variantAvailable(p, state.color, s.name));
        state.size = first ? first.name : null;
      }
    }

    function pickProduct(slot, handle) {
      const state = slots[slot];
      const p = state.list.find((x) => x.handle === handle);
      if (!p || (state.product && state.product.id === p.id)) return;
      state.product = p;
      state.color = null;
      state.size = null;
      seedVariant(slot);
      renderAll();
    }

    root.addEventListener("click", (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      const step = target.closest("[data-xstep]");
      const slot = step && step.getAttribute("data-xstep");
      if (!slot) return;

      const optBtn = target.closest("[data-xpick]");
      if (optBtn) {
        pickProduct(slot, optBtn.getAttribute("data-xpick"));
        return;
      }

      const colorBtn = target.closest("[data-xcolor]");
      if (colorBtn) {
        slots[slot].color = colorBtn.getAttribute("data-xcolor");
        seedVariant(slot);
        renderVariant(slot);
        renderStage(slot);
        renderCheckout();
        return;
      }

      const sizeBtn = target.closest("[data-xsize]");
      if (sizeBtn) {
        slots[slot].size = sizeBtn.getAttribute("data-xsize");
        renderVariant(slot);
        renderStage(slot);
        renderCheckout();
      }
    });

    /* ----- add to bag ----- */

    addBtn.addEventListener("click", async () => {
      if (adding || nextAction()) return;

      const picks = ["top", "bottom"].map((slot) => {
        const state = slots[slot];
        return {
          slot,
          product: state.product,
          color: state.color,
          size: state.size,
          variant: findVariant(state.product, state.color, state.size),
        };
      });
      if (picks.some((p) => !p.variant || !p.variant.available)) {
        toast(t("piece_sold_out", "One of those pieces is coming soon — pick another color or size"));
        renderAll();
        return;
      }

      const bundleId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `x${Date.now()}`;
      const pairLabel = t("pair_label", "Top + Bottom");
      adding = true;
      addBtn.disabled = true;
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            items: picks.map((p, i) => ({
              id: p.variant.id,
              quantity: 1,
              properties: {
                Bundle: pairLabel,
                Piece: String(i + 1),
                // Only the options this product actually has — Shopify shows
                // every non-underscored property on the order.
                ...(p.color ? { Color: p.color } : {}),
                ...(p.size ? { Size: p.size } : {}),
                _bundle_id: bundleId,
                _bundle_title: t("row_title", "Cross Bundle"),
                _bundle_label: pairLabel,
                _bundle_piece_label: p.slot === "top" ? t("top", "TOP") : t("bottom", "BOTTOM"),
                _product_title: p.product.title,
              },
            })),
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || t("could_not_add", "Could not add to bag"));

        // When the merchant configured a code, that code is what actually prices
        // the pair down — hitting /discount/CODE attaches it to this session.
        if (catalog.code) {
          try {
            await fetch(`/discount/${encodeURIComponent(catalog.code)}`);
          } catch (_) {
            // Non-fatal: an automatic discount (if one exists) still applies at checkout.
          }
        }

        toast(t("added", "Bundle added to bag"));
        if (typeof window.imanuelOpenOverlay === "function") window.imanuelOpenOverlay("cart");
      } catch (err) {
        toast(err.message || t("could_not_add", "Could not add to bag"));
      } finally {
        adding = false;
        renderCheckout();
      }
    });

    // Open on the first in-stock top so the panel shows a real price and the
    // shopper's only remaining job is the bottom.
    const firstTop = catalog.tops.find((p) => p.variants.some((v) => v.available));
    if (firstTop) {
      slots.top.product = firstTop;
      seedVariant("top");
    }

    instances.push(renderAll);
    renderAll();
  }

  function initAll() {
    document.querySelectorAll("[data-xbundle]").forEach(initCrossBundle);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Theme-editor section reloads swap the markup out from under us.
  document.addEventListener("shopify:section:load", initAll);
  // Repaint our own money nodes — they opt out of the display-currency walker.
  window.addEventListener("imanuel:display-currency", () => {
    instances.forEach((repaint) => {
      try {
        repaint();
      } catch (_) {
        /* a detached instance must not break the others */
      }
    });
  });
})();
